import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../services/api";
import { createDevConsole } from "../../../utils/devLogger";

const console = createDevConsole();

const RelationshipConfiguration = ({ data, onChange, onNext, onBack }) => {
  const configuration = data.configuration || {};
  const [entityTypes, setEntityTypes] = useState([]);
  const [entities, setEntities] = useState([]);
  const [relationshipTypes, setRelationshipTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEntities, setLoadingEntities] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (configuration.sourceEntityType) {
      loadEntitiesForType(configuration.sourceEntityType);
    }
  }, [configuration.sourceEntityType]);

  const loadInitialData = async () => {
    try {
      // Load entity types
      const typesResponse = await apiFetch("/api/entity-types");
      if (typesResponse.ok) {
        const typesData = await typesResponse.json();
        // Handle various response formats - API returns {types: [...], total: number}
        let types = [];
        if (typesData.types && Array.isArray(typesData.types)) {
          types = typesData.types;
        } else if (Array.isArray(typesData)) {
          types = typesData;
        } else if (
          typesData.entity_types &&
          Array.isArray(typesData.entity_types)
        ) {
          types = typesData.entity_types;
        } else if (typesData.data && Array.isArray(typesData.data)) {
          types = typesData.data;
        }
        // Normalize the format - API returns {value, label} format
        types = types.map((t) => ({
          id: t.id || t.value || t.name,
          name: t.value || t.name,
          display_name: t.label || t.display_name || t.name || t.value,
          description: t.description,
          icon: t.icon,
          color: t.color,
        }));
        setEntityTypes(types);
      }

      // Load relationship types
      const relResponse = await apiFetch("/api/relationship-types");
      if (relResponse.ok) {
        const relData = await relResponse.json();
        // Handle various response formats - API returns {types: [...], total: number}
        let relTypes = [];
        if (relData.types && Array.isArray(relData.types)) {
          relTypes = relData.types;
        } else if (Array.isArray(relData)) {
          relTypes = relData;
        } else if (
          relData.relationship_types &&
          Array.isArray(relData.relationship_types)
        ) {
          relTypes = relData.relationship_types;
        } else if (relData.data && Array.isArray(relData.data)) {
          relTypes = relData.data;
        }
        // Normalize the format
        relTypes = relTypes.map((r) => ({
          id: r.id || r.value || r.name,
          name: r.value || r.name,
          display_name: r.label || r.display_name || r.name || r.value,
        }));
        setRelationshipTypes(relTypes);
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadEntitiesForType = async (entityType) => {
    if (!entityType) return;

    setLoadingEntities(true);
    try {
      const response = await apiFetch(
        `/api/entities?type=${encodeURIComponent(entityType)}&limit=500`
      );
      if (response.ok) {
        const result = await response.json();
        setEntities(result.entities || result || []);
      }
    } catch (err) {
      console.error("Error loading entities:", err);
    } finally {
      setLoadingEntities(false);
    }
  };

  const handleConfigChange = (keyOrUpdates, value) => {
    // Accept either (key, value) or ({ key: value, ... })
    const updateObj =
      typeof keyOrUpdates === "string"
        ? { [keyOrUpdates]: value }
        : keyOrUpdates;

    const newConfig = {
      ...configuration,
      ...updateObj,
    };
    onChange({
      configuration: newConfig,
    });
  };

  const handleVariantChange = (variant) => {
    // Reset fields when variant changes based on what's applicable
    if (variant === "matrix") {
      // Matrix variant uses targetEntityType and analyzeAllEntities
      handleConfigChange({
        variant: variant,
        analyzeAllEntities: true,
      });
    } else {
      // Impact Analysis, Dependency, Traceability don't use targetEntityType - clear it
      handleConfigChange({
        variant: variant,
        targetEntityType: "", // Clear target entity type for variants that don't use it
      });
    }
  };

  const toggleRelationshipType = (relType) => {
    const selected = configuration.selectedRelationshipTypes || [];
    const updated = selected.includes(relType)
      ? selected.filter((r) => r !== relType)
      : [...selected, relType];
    handleConfigChange("selectedRelationshipTypes", updated);
  };

  const variants = [
    {
      id: "dependency",
      name: "Dependency Report",
      icon: "🔄",
      description: "Show what an entity depends on and what depends on it",
    },
    {
      id: "matrix",
      name: "Relationship Matrix",
      icon: "📋",
      description:
        "Cross-tabulation showing relationships between two entity types",
    },
    {
      id: "traceability",
      name: "Traceability Report",
      icon: "🔍",
      description:
        "Trace relationships across multiple entity types (e.g., Goal → Capability → App)",
    },
  ];

  const selectedVariant = configuration.variant || "dependency";

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-2">
        Relationship Analysis Configuration
      </h2>
      <p className="text-gray-400 mb-6">
        Configure your relationship analysis report
      </p>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="bg-gray-800 rounded-lg p-6 shadow-xl">
            <svg
              className="animate-spin w-8 h-8 text-blue-400 mb-3 mx-auto"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-gray-300 text-sm">
              Loading configuration options...
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Report Variant Selection */}
          <section>
            <h3 className="text-lg font-medium text-white mb-4">
              Analysis Type
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {variants.map((variant) => (
                <div
                  key={variant.id}
                  onClick={() => handleVariantChange(variant.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedVariant === variant.id
                      ? "border-blue-500 bg-blue-900/20"
                      : "border-gray-700 bg-gray-900 hover:border-gray-600"
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{variant.icon}</span>
                    <span className="font-medium text-white">
                      {variant.name}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">{variant.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Source Configuration */}
          <section>
            <h3 className="text-lg font-medium text-white mb-4">
              Source Configuration
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Source Entity Type
                </label>
                <select
                  value={configuration.sourceEntityType || ""}
                  onChange={(e) => {
                    // Update both in a single call to avoid stale closure issue
                    handleConfigChange({
                      sourceEntityType: e.target.value,
                      sourceEntityId: "", // Reset entity selection
                    });
                  }}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select entity type...</option>
                  {Array.isArray(entityTypes) &&
                    entityTypes.map((et) => (
                      <option key={et.id || et.name} value={et.name}>
                        {et.display_name || et.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* For matrix, we need a target entity type too */}
              {selectedVariant === "matrix" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Target Entity Type
                  </label>
                  <select
                    value={configuration.targetEntityType || ""}
                    onChange={(e) =>
                      handleConfigChange("targetEntityType", e.target.value)
                    }
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select target entity type...</option>
                    {Array.isArray(entityTypes) &&
                      entityTypes.map((et) => (
                        <option key={et.id || et.name} value={et.name}>
                          {et.display_name || et.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* For impact/dependency analysis, allow selecting specific entity or all */}
              {selectedVariant === "dependency" && (
                <>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="analyzeAllEntities"
                      checked={configuration.analyzeAllEntities === true}
                      onChange={(e) =>
                        handleConfigChange(
                          "analyzeAllEntities",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <label
                      htmlFor="analyzeAllEntities"
                      className="text-sm text-gray-300"
                    >
                      Analyze all entities of this type
                    </label>
                  </div>

                  {!configuration.analyzeAllEntities &&
                    configuration.sourceEntityType && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Select Specific Entity
                        </label>
                        {loadingEntities ? (
                          <div className="text-gray-400 text-sm">
                            Loading entities...
                          </div>
                        ) : (
                          <select
                            value={configuration.sourceEntityId || ""}
                            onChange={(e) =>
                              handleConfigChange(
                                "sourceEntityId",
                                e.target.value
                              )
                            }
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                          >
                            <option value="">Select entity...</option>
                            {entities.map((entity) => (
                              <option key={entity.id} value={entity.id}>
                                {entity.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                </>
              )}

              {/* For traceability, allow defining a path */}
              {selectedVariant === "traceability" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Traceability Path (in order)
                  </label>
                  <div className="bg-gray-900 rounded-lg border border-gray-700 p-4">
                    <div className="space-y-2">
                      {(configuration.traceabilityPath || []).map(
                        (step, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-gray-400 w-6">
                              {idx + 1}.
                            </span>
                            <select
                              value={step}
                              onChange={(e) => {
                                const path = [
                                  ...(configuration.traceabilityPath || []),
                                ];
                                path[idx] = e.target.value;
                                handleConfigChange("traceabilityPath", path);
                              }}
                              className="flex-1 px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                            >
                              <option value="">Select entity type...</option>
                              {Array.isArray(entityTypes) &&
                                entityTypes.map((et) => (
                                  <option
                                    key={et.id || et.name}
                                    value={et.name}
                                  >
                                    {et.display_name || et.name}
                                  </option>
                                ))}
                            </select>
                            <button
                              onClick={() => {
                                const path = (
                                  configuration.traceabilityPath || []
                                ).filter((_, i) => i !== idx);
                                handleConfigChange("traceabilityPath", path);
                              }}
                              className="text-red-400 hover:text-red-300"
                            >
                              ✕
                            </button>
                          </div>
                        )
                      )}
                      <button
                        onClick={() => {
                          const path = [
                            ...(configuration.traceabilityPath || []),
                            "",
                          ];
                          handleConfigChange("traceabilityPath", path);
                        }}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        + Add step
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Analysis Options */}
          <section>
            <h3 className="text-lg font-medium text-white mb-4">
              Analysis Options
            </h3>
            <div className="space-y-4">
              {/* Depth setting for impact/dependency/traceability */}
              {["dependency", "traceability"].includes(selectedVariant) && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Analysis Depth (hops)
                  </label>
                  <select
                    value={configuration.analysisDepth || 2}
                    onChange={(e) =>
                      handleConfigChange(
                        "analysisDepth",
                        parseInt(e.target.value)
                      )
                    }
                    className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value={1}>1 hop (direct relationships only)</option>
                    <option value={2}>2 hops (include indirect)</option>
                    <option value={3}>3 hops (extended analysis)</option>
                    <option value={4}>4 hops (deep analysis)</option>
                  </select>
                </div>
              )}

              {/* Direction for impact/dependency */}
              {selectedVariant === "dependency" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Analysis Direction
                  </label>
                  <div className="flex gap-4">
                    {[
                      { id: "both", label: "Both directions" },
                      {
                        id: "downstream",
                        label: "Downstream (what depends on this)",
                      },
                      {
                        id: "upstream",
                        label: "Upstream (what this depends on)",
                      },
                    ].map((dir) => (
                      <label
                        key={dir.id}
                        className="flex items-center gap-2 text-sm text-gray-300"
                      >
                        <input
                          type="radio"
                          name="direction"
                          value={dir.id}
                          checked={
                            (configuration.analysisDirection || "both") ===
                            dir.id
                          }
                          onChange={(e) =>
                            handleConfigChange(
                              "analysisDirection",
                              e.target.value
                            )
                          }
                          className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700"
                        />
                        {dir.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Relationship type filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Relationship Types to Include
                </label>
                <div className="bg-gray-900 rounded-lg border border-gray-700 p-4 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <input
                        type="checkbox"
                        checked={
                          (configuration.selectedRelationshipTypes || [])
                            .length === 0
                        }
                        onChange={() =>
                          handleConfigChange("selectedRelationshipTypes", [])
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
                      />
                      <span className="font-medium">
                        All relationship types
                      </span>
                    </label>
                    <div className="border-t border-gray-700 pt-2">
                      {Array.isArray(relationshipTypes) &&
                        relationshipTypes.map((rel) => (
                          <label
                            key={rel.id || rel.name}
                            className="flex items-center gap-2 text-sm text-gray-300"
                          >
                            <input
                              type="checkbox"
                              checked={(
                                configuration.selectedRelationshipTypes || []
                              ).includes(rel.name)}
                              onChange={() => toggleRelationshipType(rel.name)}
                              className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
                            />
                            {rel.display_name || rel.name}
                          </label>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Output options */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Output Options
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={configuration.showRelationshipCounts !== false}
                      onChange={(e) =>
                        handleConfigChange(
                          "showRelationshipCounts",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
                    />
                    Show relationship counts
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={configuration.groupByEntityType !== false}
                      onChange={(e) =>
                        handleConfigChange(
                          "groupByEntityType",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
                    />
                    Group results by entity type
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={configuration.includeEntityDetails === true}
                      onChange={(e) =>
                        handleConfigChange(
                          "includeEntityDetails",
                          e.target.checked
                        )
                      }
                      className="w-4 h-4 text-blue-600 bg-gray-900 border-gray-700 rounded"
                    />
                    Include full entity details
                  </label>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!configuration.sourceEntityType}
          className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
            configuration.sourceEntityType
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-700 text-gray-500 cursor-not-allowed"
          }`}
        >
          Next: Preview
        </button>
      </div>
    </div>
  );
};

export default RelationshipConfiguration;
